/**
 * QuestionChooser — frame picker for the chat surface.
 *
 * gibbs-question-chooser study (2026-05-15). Renders the
 * FRAMES catalog as a modal-style picker. Activated from a small
 * indicator chip in the ChatBot header.
 *
 * @module ui/QuestionChooser
 */

import { useState, useCallback, useEffect } from 'react';
import {
    Box, Typography, Button, Chip, Dialog, DialogTitle,
    DialogContent, Card, CardActionArea, CardContent,
} from '@mui/material';
import {
    FRAMES, getActiveFrame, setActiveFrame,
} from '../gibbs/question-chooser';

/**
 * Small inline chip showing the current frame. Click to open the
 * full picker dialog. Tooltip-style description on hover via the
 * Chip's title attribute fallback.
 *
 * @param {Object} props
 * @param {() => void} [props.onChange] - Called after a frame change
 *   is persisted (so callers can refresh derived state).
 */
export function ActiveFrameChip({ onChange }) {
    const [open, setOpen] = useState(false);
    const [activeId, setActiveId] = useState(() => {
        const f = getActiveFrame();
        return f ? f.id : null;
    });

    // Refresh on remount in case another surface changed the frame.
    useEffect(() => {
        const f = getActiveFrame();
        setActiveId(f ? f.id : null);
    }, [open]);

    const handlePick = useCallback(async (frameId) => {
        await setActiveFrame(frameId);
        const f = getActiveFrame();
        setActiveId(f ? f.id : null);
        setOpen(false);
        if (onChange) onChange();
    }, [onChange]);

    const activeFrame = FRAMES.find((f) => f.id === activeId);
    const chipLabel = activeFrame
        ? `Frame: ${activeFrame.label.length > 24
            ? `${activeFrame.label.slice(0, 22)}…`
            : activeFrame.label}`
        : 'Pick frame';

    return (
        <>
            <Chip
                label={chipLabel}
                size="small"
                color={activeFrame ? 'primary' : 'default'}
                onClick={() => setOpen(true)}
                sx={{ fontSize: '0.72em', height: 22 }}
                aria-label={
                    activeFrame
                        ? `Active decision frame: ${activeFrame.label}. Click to change.`
                        : 'Pick a decision frame to focus the assistant.'
                }
            />
            <FramePickerDialog
                open={open}
                onClose={() => setOpen(false)}
                activeId={activeId}
                onPick={handlePick}
            />
        </>
    );
}

function FramePickerDialog({ open, onClose, activeId, onPick }) {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            aria-labelledby="question-chooser-title"
        >
            <DialogTitle id="question-chooser-title">
                What are you trying to decide?
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Pick a frame to focus the assistant. Sets the prompt
                    bias for this session — switch any time.
                </Typography>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                }}>
                    {FRAMES.map((f) => (
                        <Card
                            key={f.id}
                            variant={activeId === f.id ? 'elevation' : 'outlined'}
                            elevation={activeId === f.id ? 4 : 0}
                            sx={{
                                borderColor: activeId === f.id ? 'primary.main' : 'divider',
                            }}
                        >
                            <CardActionArea onClick={() => onPick(f.id)}>
                                <CardContent sx={{ py: 1.25, px: 1.5 }}>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: 600, mb: 0.5 }}
                                    >
                                        {f.label}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {f.description}
                                    </Typography>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    ))}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button onClick={onClose} size="small">
                        Cancel
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
}

export default ActiveFrameChip;
